import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Search, ChevronLeft, ChevronRight, Lock, ShieldCheck, LogOut } from 'lucide-react';
import { enqueteDinamicaAPI } from '../utils/enqueteDinamicaAPI';
import { authAPI } from '../utils/adminAPI';
import { useIsMobile } from '../hooks/useIsMobile';
import EnquetePollCard from '../components/home/EnquetePollCard';
import ComoFunciona from '../components/home/ComoFunciona';

const ITENS_POR_PAGINA_DESKTOP = 6; // até 3 por linha, 2 linhas
const ITENS_POR_PAGINA_MOBILE = 4; // carrossel lateral, 4 por página

const PaginaInicial = () => {
  const [enquetes, setEnquetes] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(false);
  const [bloqueadoAdmin, setBloqueadoAdmin] = useState(false);
  const [busca, setBusca] = useState('');
  const [pagina, setPagina] = useState(1);

  const isMobile = useIsMobile();
  const itensPorPagina = isMobile ? ITENS_POR_PAGINA_MOBILE : ITENS_POR_PAGINA_DESKTOP;

  useEffect(() => {
    let ativo = true;

    enqueteDinamicaAPI.listarPublicas()
      .then((dados) => {
        if (ativo) setEnquetes(dados.enquetes || []);
      })
      .catch((error) => {
        if (!ativo) return;
        if (error.response?.data?.code === 'ADMIN_SESSION_ACTIVE') {
          setBloqueadoAdmin(true);
        } else {
          setErro(true);
        }
      })
      .finally(() => {
        if (ativo) setCarregando(false);
      });

    return () => { ativo = false; };
  }, []);

  const handleLogoutEVoltar = async () => {
    await authAPI.logout();
    window.location.reload();
  };

  const enquetesFiltradas = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    if (!termo) return enquetes;
    return enquetes.filter((e) => e.titulo.toLowerCase().includes(termo));
  }, [enquetes, busca]);

  const totalPaginas = Math.max(1, Math.ceil(enquetesFiltradas.length / itensPorPagina));

  // Volta pra página 1 sempre que a busca muda ou o layout muda de
  // desktop pra mobile (tamanho de página diferente).
  useEffect(() => {
    setPagina(1);
  }, [busca, itensPorPagina]);

  useEffect(() => {
    if (pagina > totalPaginas) setPagina(totalPaginas);
  }, [pagina, totalPaginas]);

  const itensDaPagina = enquetesFiltradas.slice(
    (pagina - 1) * itensPorPagina,
    pagina * itensPorPagina
  );

  if (bloqueadoAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-br from-primary-50 to-blue-50">
        <div className="card max-w-md text-center">
          <ShieldCheck className="w-12 h-12 text-primary-600 mx-auto mb-3" />
          <h1 className="text-lg font-bold text-gray-900 mb-2">Você está logado como administrador</h1>
          <p className="text-gray-600 mb-4">
            Para ver a página inicial como visitante, saia da sua sessão de admin primeiro.
          </p>
          <button onClick={handleLogoutEVoltar} className="btn-primary inline-flex items-center gap-2">
            <LogOut className="w-4 h-4" /> Sair da sessão de admin
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Enquetes Públicas - Prefeitura de Corumbá MS</title>
        <meta name="description" content="Participe das enquetes públicas da Prefeitura de Corumbá. Sua opinião é fundamental para o desenvolvimento da nossa cidade." />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-blue-50 relative">
        {/* Fundo de página — mesma convenção de opacidade fixa e baixa
            (opacity-10) já usada em FundoPersonalizado.js pras enquetes
            dinâmicas, pra nunca competir com o conteúdo/legibilidade por
            cima. Não é bg-fixed (parallax) de propósito — background-
            attachment:fixed tem suporte inconsistente no Safari mobile,
            mesma razão pela qual FundoPersonalizado.js também não usa. */}
        <div
          className="absolute inset-0 bg-cover bg-center opacity-10 pointer-events-none"
          style={{ backgroundImage: 'url(/assets/foto_backgorund_pagina_inical.jpeg)' }}
          aria-hidden="true"
        />

        {/* flex flex-col + flex-1 no <main>: padrão "sticky footer" — sem
            isso, o footer só vem logo depois do conteúdo em fluxo normal, e
            o min-h-screen da div de fora acaba sobrando como espaço vazio
            DEPOIS do footer (só o fundo aparecendo) em vez de empurrá-lo
            pro final de verdade, quando o conteúdo é curto (poucas
            enquetes, tela mais baixa/com zoom reduzido). */}
        <div className="relative z-10 flex flex-col min-h-screen">
        {/* Header */}
        <header className="bg-primary text-white shadow-lg">
          <div className="container-responsive py-8">
            <div className="flex flex-col items-center text-center">
              <Link to="https://corumba.ms.gov.br/">
              <img
                src="/assets/logo_prefeitura_horizontal.png"
                alt="Prefeitura de Corumbá"
                className="h-16 sm:h-20 w-auto mb-4"
              /></Link>
              <h1 className="text-3xl sm:text-4xl font-bold">
                Enquetes Públicas
              </h1>
              <p className="text-lg sm:text-xl text-primary-100 mt-2">
                Sua voz importa para o futuro de Corumbá
              </p>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="container-responsive py-12 flex-1">
          <div className="max-w-5xl mx-auto">

            {/* Introdução */}
            <div className="text-center mb-10 animate-fade-in-up">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
                Participe das Decisões da Cidade
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                A Prefeitura de Corumbá valoriza a participação popular. Escolha uma das enquetes públicas abaixo e contribua com sua opinião.
              </p>
            </div>

            {/* Busca */}
            {!carregando && !erro && enquetes.length > 0 && (
              <div className="max-w-md mx-auto mb-10 relative">
                <Search className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  placeholder="Pesquisar enquete pelo nome..."
                  className="input-field pl-10"
                />
              </div>
            )}
          </div>

          {/* Cards das Enquetes — largura própria, mais ampla que o resto do
              conteúdo, pra caber confortavelmente 3 colunas no desktop. */}
          <div className="max-w-6xl mx-auto">
            {carregando && (
              <div className="flex justify-center mb-12">
                <div className="loading-spinner" />
              </div>
            )}

            {!carregando && erro && (
              <div className="card bg-red-50 border-2 border-red-200 text-center mb-12 max-w-5xl mx-auto">
                <p className="text-red-700">
                  Não foi possível carregar as enquetes públicas no momento. Tente novamente mais tarde.
                </p>
              </div>
            )}

            {!carregando && !erro && enquetes.length === 0 && (
              <div className="card bg-gray-50 border-2 border-gray-200 text-center mb-12 max-w-5xl mx-auto">
                <p className="text-gray-600">
                  Nenhuma consulta pública disponível no momento. Volte em breve!
                </p>
              </div>
            )}

            {!carregando && !erro && enquetes.length > 0 && enquetesFiltradas.length === 0 && (
              <div className="card bg-gray-50 border-2 border-gray-200 text-center mb-12 max-w-5xl mx-auto">
                <p className="text-gray-600">
                  Nenhuma enquete encontrada para "{busca}".
                </p>
              </div>
            )}

            {itensDaPagina.length > 0 && (
              <>
                {isMobile ? (
                  <div className="flex overflow-x-auto snap-x snap-mandatory gap-4 px-4 pb-4 -mx-4">
                    {itensDaPagina.map((enquete) => (
                      <div
                        key={enquete.slug || enquete.rota}
                        className="snap-center shrink-0 w-[85%] max-w-sm"
                      >
                        <EnquetePollCard enquete={enquete} />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-6 px-4">
                    {itensDaPagina.map((enquete) => (
                      <EnquetePollCard key={enquete.slug || enquete.rota} enquete={enquete} />
                    ))}
                  </div>
                )}

                {totalPaginas > 1 && (
                  <div className="flex items-center justify-center gap-4 mt-8">
                    <button
                      onClick={() => setPagina((p) => Math.max(1, p - 1))}
                      disabled={pagina === 1}
                      className="p-2 rounded-full bg-white shadow disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-50"
                      aria-label="Página anterior"
                    >
                      <ChevronLeft className="w-5 h-5 text-gray-700" />
                    </button>

                    <div className="flex items-center gap-2">
                      {Array.from({ length: totalPaginas }, (_, i) => i + 1).map((n) => (
                        <button
                          key={n}
                          onClick={() => setPagina(n)}
                          aria-label={`Página ${n}`}
                          className={`w-2.5 h-2.5 rounded-full transition-colors ${
                            n === pagina ? 'bg-primary-600' : 'bg-gray-300 hover:bg-gray-400'
                          }`}
                        />
                      ))}
                    </div>

                    <button
                      onClick={() => setPagina((p) => Math.min(totalPaginas, p + 1))}
                      disabled={pagina === totalPaginas}
                      className="p-2 rounded-full bg-white shadow disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-50"
                      aria-label="Próxima página"
                    >
                      <ChevronRight className="w-5 h-5 text-gray-700" />
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="max-w-5xl mx-auto">
            <ComoFunciona />

            {/* Informações de Segurança */}
            <div className="mt-8 text-center text-sm text-gray-500 animate-fade-in-up">
              <p className="mb-2">
                🔒 Suas respostas são anônimas e seguras
              </p>
              <p>
                ✅ Sistema com proteção anti-fraude e conformidade com a LGPD
              </p>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="bg-gray-800 text-white py-8 mt-12">
          <div className="container-responsive text-center">
            <p className="text-gray-300">
              © 2026 Prefeitura Municipal de Corumbá - Todos os direitos reservados
            </p>
            <p className="text-gray-400 text-sm mt-2">
              Desenvolvido com ❤️ para a cidade de Corumbá-MS
            </p>

            {/* Acesso do gerenciador/admin */}
            <Link
              to="/admin/login"
              className="inline-flex items-center gap-2 text-white text-sm font-medium border-2 border-gray-500 hover:border-[#40A1E6] rounded-full px-5 py-2 mt-6 hover:bg-[#40A1E6]/10 hover:shadow-lg hover:shadow-[#40A1E6]/20 transition-all duration-200"
            >
              <Lock className="w-4 h-4" /> Acesso restrito
            </Link>
          </div>
        </footer>
        </div>
      </div>
    </>
  );
};

export default PaginaInicial;
